import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NSBadge, NSButton, NSInput } from 'bricks';
import RenderIf from 'components/RenderIf/RenderIf';
import {
    faCheck, faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { capitalize } from 'lodash';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from 'contexts/AuthContext';
import useToast from 'hooks/useToast';
import { useUpdateTimeline } from '../../../hooks/useUpdateTimeline';
import { useDeleteTimeline } from '../../../hooks/useDeleteTimeline';
import { useCreateTimeline } from '../../../hooks/useCreateTimeline';
import { useAssignUser } from 'hooks/useAssignUser';
import { useUnassignUser } from 'hooks/useUnassignUser';
import ActionMenu from '../ActionMenu/ActionMenu';
import TimelineDatePicker from '../TimelineDatePicker/TimelineDatePicker';
import AssigneeCell from '../AssigneeCell/AssigneeCell';
import {
    TEMP_TIMELINE_ID_PREFIX, DateField, TimelineStatus, TimelineStatusBootstrapColorMap,
} from '../../../../constants';
import { TimelineAssignee } from 'types/timeline';

type TimelineItem = {
    id: string;
    dealId: number;
    name: string;
    target: string | null;
    completed: string | null;
    status: TimelineStatus;
    assignees?: TimelineAssignee[];
};

type TimelineActivityRowProps = {
    item: TimelineItem;
    dealId: number;
    selectedTimelineId: string | number | null;
    setSelectedTimelineId: React.Dispatch<React.SetStateAction<string | number | null>>;
    onTimelineDataUpdate: (updater: (prev: TimelineItem[]) => TimelineItem[]) => void;
};

const TimelineActivityRow = ({
    item, dealId, selectedTimelineId, setSelectedTimelineId, onTimelineDataUpdate,
}: TimelineActivityRowProps) => {
    const { selectedOrganizationId } = useAuthContext();
    const queryClient = useQueryClient();
    const { showError } = useToast();
    const { mutate: createTimeline } = useCreateTimeline();
    const { mutate: deleteTimelineMutation } = useDeleteTimeline();

    const numericTimelineId = Number(item.id);
    const safeTimelineId = Number.isNaN(numericTimelineId) ? 0 : numericTimelineId;

    const { mutate: assignUser } = useAssignUser({ orgId: selectedOrganizationId!, dealId, timelineId: safeTimelineId });

    const { mutate: unassignUser } = useUnassignUser({ orgId: selectedOrganizationId!, dealId, timelineId: safeTimelineId });

    const onUpdateSuccess = (updatedTimeline: TimelineItem) => {
        onTimelineDataUpdate(prevTimeline => prevTimeline.map(timelineItem => (
            timelineItem.id === updatedTimeline.id
                ? { ...timelineItem, ...updatedTimeline, assignees: timelineItem.assignees }
                : timelineItem
        )));
    };

    const { mutate: updateTimeline } = useUpdateTimeline(onUpdateSuccess);
    const [currentItemName, setCurrentItemName] = useState<string | null>(item.name);
    const [currentItemTargetDate, setCurrentItemTargetDate] = useState<Date | null>(item.target ? new Date(item.target) : null);
    const [currentItemCompletedDate, setCurrentItemCompletedDate] = useState<Date | null>(item.completed ? new Date(item.completed) : null);
    const isNewTimelineActivity = (id: string | null | undefined) => Boolean(id && String(id).startsWith(TEMP_TIMELINE_ID_PREFIX));
    const selectedTimelineIsValid = !!selectedTimelineId && !!currentItemName;

    const formatDateForAPI = (date: Date): string => {
        const offset = date.getTimezoneOffset();
        const adjustedDate = new Date(date.getTime() - (offset * 60 * 1000));
        return adjustedDate.toISOString().split('T')[0];
    };

    const handleAssignUser = useCallback((userId: string) => {
        if (isNewTimelineActivity(item.id) || safeTimelineId === 0) return;
        assignUser(
            { userCorrelationId: userId },
            {
                onSuccess: (newAssignee) => {
                    onTimelineDataUpdate(prevTimelines =>
                        prevTimelines.map(t => t.id === item.id ? { ...t, assignees: [...(t.assignees || []), newAssignee] } : t),
                    );
                },
            },
        );
    }, [assignUser, onTimelineDataUpdate, item.id, safeTimelineId]);

    const handleUnassignUser = useCallback((userId: string) => {
        if (isNewTimelineActivity(item.id) || safeTimelineId === 0) return;
        unassignUser(userId, {
            onSuccess: () => {
                onTimelineDataUpdate(prevTimelines =>
                    prevTimelines.map(t =>
                        t.id === item.id ? { ...t, assignees: (t.assignees || []).filter(a => a.userCorrelationId !== userId) }: t,
                    ),
                );
            },
        });
    }, [unassignUser, onTimelineDataUpdate, item.id, safeTimelineId]);

    const handleDelete = (id: string) => {
        deleteTimelineMutation(
            {
                orgId: selectedOrganizationId!,
                dealId,
                timelineId: id,
            },
            {
                onSuccess: () => {
                    onTimelineDataUpdate(prevTimeline => prevTimeline.filter(timelineItem => timelineItem.id !== id));
                    queryClient.invalidateQueries({ queryKey: ['timelines', selectedOrganizationId!, dealId] });
                },
                onError: (error: any) => {
                    console.error(`Failed to delete timeline with ID: ${id}`, error);
                },
            },
        );
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCurrentItemName(event.target.value);
    };

    const handleRowKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && selectedTimelineIsValid) {
            handleSaveEdit();
        } else if (event.key === 'Escape') {
            handleCancelEdit();
        }
    };

    const handleCancelEdit = () => {
        setSelectedTimelineId(null);
        setCurrentItemName(item.name);
        setCurrentItemTargetDate(item.target ? new Date(item.target) : null);
        setCurrentItemCompletedDate(item.completed ? new Date(item.completed) : null);
        if (isNewTimelineActivity(item.id)) {
            onTimelineDataUpdate(prevTimeline => prevTimeline.filter(timelineItem => timelineItem.id !== item.id));
        }
    };

    const handleSaveEdit = () => {
        const name = currentItemName || '';
        const target = currentItemTargetDate ? formatDateForAPI(currentItemTargetDate) : null;
        const completed = currentItemCompletedDate ? formatDateForAPI(currentItemCompletedDate) : null;

        // Save to API
        if (isNewTimelineActivity(item.id)) {
            createTimeline(
                {
                    orgId: selectedOrganizationId!,
                    dealId,
                    postData: { name, target, completed },
                },
                {
                    onSuccess: (createdTimeline: TimelineItem) => {
                        onTimelineDataUpdate(prevTimeline => [
                            createdTimeline,
                            ...prevTimeline.filter(timelineItem => timelineItem.id !== item.id),
                        ]);
                        queryClient.invalidateQueries({ queryKey: ['timelines', selectedOrganizationId!, dealId] });
                        setSelectedTimelineId(null);
                    },
                    onError: (error: any) => {
                        showError('Failed to create timeline');
                    },
                },
            );
        } else {
            updateTimeline(
                {
                    orgId: selectedOrganizationId!,
                    dealId,
                    timelineId: item.id,
                    patchData: { name, target, completed },
                },
                {
                    onSuccess: () => {
                        queryClient.invalidateQueries({ queryKey: ['timelines', selectedOrganizationId!, dealId] });
                        setSelectedTimelineId(null);
                    },
                    onError: (error: any) => {
                        console.error(`Failed to update timeline with ID: ${item.id}`, error);
                        showError('Failed to update timeline');
                    },
                },
            );
        }
    };

    const isEditing = !!selectedTimelineId;
    const isSelectedToEdit = selectedTimelineId === item.id;
    const noOp = () => {};

    return (
        <tr key={item.id} className="timeline-row">
            <RenderIf isTrue={isSelectedToEdit}>
                <td>
                    <NSInput
                        type="text"
                        name={`timeline-input-${item.id}`}
                        id={`timeline-input-${item.id}`}
                        value={currentItemName || ''}
                        onChange={handleNameChange}
                        onKeyDown={handleRowKeyPress}
                    />
                </td>
                <td>
                    <TimelineDatePicker
                        value={currentItemTargetDate}
                        onChange={date => setCurrentItemTargetDate(date)}
                        field={DateField.TARGET}
                        isEditing
                        onEdit={() => setSelectedTimelineId(item.id)}
                        onKeyDown={handleRowKeyPress}
                        placeholder="Select target date"
                    />
                </td>
                <td>
                    <TimelineDatePicker
                        value={currentItemCompletedDate}
                        onChange={date => setCurrentItemCompletedDate(date)}
                        field={DateField.COMPLETED}
                        isEditing
                        onEdit={() => setSelectedTimelineId(item.id)}
                        onKeyDown={handleRowKeyPress}
                        placeholder="Select completed date"
                    />
                </td>
                <td>
                    <AssigneeCell
                        orgId={selectedOrganizationId!}
                        assignees={item.assignees || []}
                        onAssign={handleAssignUser}
                        onRemove={handleUnassignUser}
                        disabled={isNewTimelineActivity(item.id)}
                    />
                </td>
                <td>
                    <NSBadge
                        className="ml-1"
                        color={TimelineStatusBootstrapColorMap[item.status] || 'primary-lighten'}
                    >
                        {capitalize(item.status)}
                    </NSBadge>
                </td>
                <td>
                    <NSButton
                        aria-label="Cancel"
                        className="p-0 mx-1 timeline-btn"
                        size="sm"
                        color="danger"
                        outline
                        callback={handleCancelEdit}
                    >
                        <FontAwesomeIcon icon={faXmark} size="sm" />
                    </NSButton>
                    <NSButton
                        id="save-timeline-btn"
                        aria-label="Save"
                        className="p-0 timeline-btn mr-1"
                        size="sm"
                        color="success"
                        outline
                        disabled={!selectedTimelineIsValid}
                        disabledTooltip="Activity name is required"
                        callback={handleSaveEdit}
                    >
                        <FontAwesomeIcon icon={faCheck} size="sm" />
                    </NSButton>
                </td>
            </RenderIf>
            <RenderIf isTrue={!isSelectedToEdit}>
                <td>
                    <span
                        onClick={isEditing ? noOp : () => setSelectedTimelineId(item.id)}
                        onKeyDown={isEditing ? noOp : e => e.key === 'Enter' && setSelectedTimelineId(item.id)}
                        role="button"
                        tabIndex={0}
                        className="cursor--pointer"
                    >
                        {item.name}
                    </span>
                </td>
                <td>
                    <TimelineDatePicker
                        value={item.target ? new Date(item.target) : null}
                        onChange={date => setCurrentItemTargetDate(date)}
                        field={DateField.TARGET}
                        isEditing={false}
                        onEdit={() => setSelectedTimelineId(item.id)}
                        disabled={isEditing}
                    />
                </td>
                <td>
                    <TimelineDatePicker
                        value={item.completed ? new Date(item.completed) : null}
                        onChange={date => setCurrentItemCompletedDate(date)}
                        field={DateField.COMPLETED}
                        isEditing={false}
                        onEdit={() => setSelectedTimelineId(item.id)}
                        disabled={isEditing}
                    />
                </td>
                <td>
                    <AssigneeCell
                        orgId={selectedOrganizationId!}
                        assignees={item.assignees || []}
                        onAssign={handleAssignUser}
                        onRemove={handleUnassignUser}
                        disabled={isEditing || isNewTimelineActivity(item.id)}
                    />
                </td>
                <td>
                    <NSBadge
                        className="ml-1"
                        color={TimelineStatusBootstrapColorMap[item.status] || 'primary-lighten'}
                    >
                        {capitalize(item.status)}
                    </NSBadge>
                </td>
                <td>
                    <RenderIf isTrue={!isEditing}>
                        <ActionMenu
                            onView={() => setSelectedTimelineId(item.id)}
                            onDelete={() => handleDelete(item.id)}
                        />
                    </RenderIf>
                </td>
            </RenderIf>
        </tr>
    );
};
export default TimelineActivityRow;
